import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle } from "lucide-react";
import { CONTACTS, IMG, EVENTS, SUPPLIER } from "../data/site";
import { CTAButton } from "./ui-bits";
import { Reveal } from "./anim";

const QUICK = [
  { to: "/concept", label: "Il concept" },
  { to: "/cocktail-e-servizi", label: "Cocktail e servizi" },
  { to: "/allestimenti", label: "Allestimenti" },
  { to: "/preventivo", label: "Preventivo online" },
  { to: "/partner", label: "Partner professionali" },
  { to: "/gallery", label: "Gallery" },
  { to: "/privacy", label: "Privacy e cookie" },
];

export default function Footer() {
  const { pathname } = useLocation();
  const isPubblici = pathname === "/eventi-pubblici";
  return (
    <footer className="bg-brand-coal border-t border-white/[0.08]">
      <div className="mx-auto max-w-7xl px-5 md:px-8 pt-20 md:pt-28 pb-10">
        <Reveal>
          <Link to="/contatti" data-testid="footer-cta-link" className="block group max-w-5xl">
            <p className="eyebrow mb-6">Il prossimo brindisi è tuo</p>
            <h2 className="font-display font-semibold leading-[0.98] tracking-tight text-[clamp(2.6rem,7vw,6.5rem)]">
              IL TUO EVENTO MERITA
              <br />
              <span className="italic text-brand-orange transition-colors duration-300 group-hover:text-[#ff9433]">
                IL SUO SPRIZZIAMOCI.
              </span>
            </h2>
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <div className="flex flex-wrap gap-4">
            {isPubblici ? (
              <CTAButton to="/partner" testId="footer-cta-quote">
                Raccontaci il tuo evento
              </CTAButton>
            ) : (
              <>
                <CTAButton to="/contatti" testId="footer-cta-quote">
                  Richiedi un preventivo
                </CTAButton>
                <CTAButton to="/partner" variant="outline" testId="footer-cta-partner">
                  Diventa partner socio
                </CTAButton>
              </>
            )}
          </div>
        </Reveal>

        <div className="mt-20 md:mt-28 grid gap-12 md:grid-cols-12 border-t border-white/[0.08] pt-14">
          <div className="md:col-span-5">
            <img src={IMG.logo} alt="Sprizziamoci" className="h-14 w-auto mix-blend-screen" />
            <p className="mt-4 font-display italic text-xl text-brand-beige">L'aperitivo on the road</p>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/50">
              Cocktail e bevande alla spina, direttamente dove si svolge la festa.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={CONTACTS.phoneHref}
                data-testid="footer-phone-link"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white/80 hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
              >
                <Phone size={14} /> {CONTACTS.phoneDisplay}
              </a>
              <a
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white/80 hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
            <a
              href={SUPPLIER.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-supplier-link"
              className="mt-8 flex w-full max-w-md items-center gap-5 rounded-2xl border border-white/[0.08] p-5 pr-6 group hover:border-brand-orange/40 transition-colors duration-300"
            >
              <span className="flex items-center rounded-xl bg-brand-ivory px-4 py-3">
                <img src="/pasquini.png" alt="Pasquini SRL" className="h-12 w-auto" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {SUPPLIER.tag}
                </span>
                <span className="block mt-1 text-sm leading-snug text-white/70 group-hover:text-brand-orange transition-colors duration-300">
                  Bevande e impianti di spillatura Ho.Re.Ca — pasquinisrl.it
                </span>
              </span>
            </a>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow mb-6">Link rapidi</p>
            <ul className="space-y-3">
              {QUICK.filter((q) => !(isPubblici && q.to === "/preventivo")).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace("/", "")}`}
                    className="text-sm text-white/60 hover:text-brand-orange transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="eyebrow mb-6">Per ogni evento</p>
            <ul className="space-y-3">
              {EVENTS.map((e) => (
                <li key={e.slug}>
                  <Link
                    to={e.path}
                    data-testid={`footer-link-${e.slug}`}
                    className="text-sm text-white/60 hover:text-brand-orange transition-colors duration-200"
                  >
                    {e.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/contatti"
                  data-testid="footer-link-contatti"
                  className="text-sm text-white/60 hover:text-brand-orange transition-colors duration-200"
                >
                  Contatti e preventivi
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 border-t border-white/[0.08] pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Sprizziamoci — L'aperitivo on the road. Tutti i diritti riservati.
          </p>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-white/40 hover:text-brand-orange transition-colors duration-200">
              Informativa privacy e cookie
            </Link>
            <span className="text-xs text-white/25">[Dati societari e informazioni legali — in arrivo]</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
